import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, type ApiError, type ListIpAllowlistResult } from '@whinerp/client';
import { Button, Panel, SectionLabel } from '@whinerp/ui';
import { useT } from '../../app/i18n.js';
import { useSession } from '../../app/session.js';

/**
 * The networks this workspace may be used from (G-282). Says the address the
 * administrator is on, because the one range they must not forget is theirs.
 */
export function IpAllowlistPanel(): JSX.Element | null {
  const t = useT();
  const { can } = useSession();
  const queryClient = useQueryClient();
  const enabled = can('setup.users.manage');
  const [cidr, setCidr] = useState('');
  const [label, setLabel] = useState('');
  const list = useQuery<ListIpAllowlistResult, ApiError>({ queryKey: ['ip-allowlist'], queryFn: () => api.listIpAllowlist({}), enabled });
  const refresh = () => queryClient.invalidateQueries({ queryKey: ['ip-allowlist'] });
  const add = useMutation<unknown, ApiError>({
    mutationFn: () => api.addIpAllowlistEntry({ body: { cidr: cidr.trim(), label: label.trim() } }),
    onSuccess: async () => {
      setCidr('');
      setLabel('');
      await refresh();
    },
  });
  const remove = useMutation<unknown, ApiError, string>({ mutationFn: (entryId) => api.removeIpAllowlistEntry({ path: { entryId } }), onSuccess: () => refresh() });
  if (!enabled || !list.data) return null;
  const failure = add.error ?? remove.error;
  const INPUT = 'rounded-control border border-rule bg-surface px-2 py-1 text-body text-ink';
  return (
    <Panel data-region="ip-allowlist">
      <div className="flex flex-col gap-3">
        <div className="flex flex-col gap-1">
          <SectionLabel>{t('ipAllowlist.title')}</SectionLabel>
          <p className="text-meta text-ink-meta">{t('ipAllowlist.body')}</p>
          <p className="text-meta text-ink" data-your-ip>{t('ipAllowlist.yourIp', { values: { ip: list.data.yourIp } })}</p>
        </div>
        {list.data.entries.length === 0 ? (
          <p className="text-dense text-ink-meta" data-allowlist-off>{t('ipAllowlist.off')}</p>
        ) : (
          <ul className="flex flex-col">
            {list.data.entries.map((entry) => (
              <li key={entry.entryId} data-allowlist-entry={entry.cidr} className="flex items-center gap-2 border-t border-t-rule py-1 first:border-t-0">
                <span className="font-mono text-dense text-ink">{entry.cidr}</span>
                <span className="text-meta text-ink-meta">{entry.label}</span>
                <Button intent="ghost" size="small" className="ms-auto" onClick={() => remove.mutate(entry.entryId)}>{t('action.delete')}</Button>
              </li>
            ))}
          </ul>
        )}
        {failure ? <p data-allowlist-refusal className="rounded-control bg-danger-tint px-3 py-2 text-dense text-danger-text">{failure.message}</p> : null}
        <div className="flex flex-wrap items-end gap-2">
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('ipAllowlist.range')}
            <input data-allowlist-cidr className={INPUT} placeholder="203.0.113.0/24" value={cidr} onChange={(event) => setCidr(event.target.value)} />
          </label>
          <label className="flex flex-col gap-1 text-dense font-medium text-ink">
            {t('ipAllowlist.label')}
            <input data-allowlist-label className={INPUT} value={label} onChange={(event) => setLabel(event.target.value)} />
          </label>
          <Button intent="secondary" size="small" data-allowlist-add disabled={!cidr.trim() || !label.trim()} onClick={() => add.mutate()}>
            {t('ipAllowlist.add')}
          </Button>
        </div>
      </div>
    </Panel>
  );
}
